import { v4 as uuidv4 } from 'uuid';
import {
  InternalQuery,
  InternalRow,
  InternalRule,
  SearchContext,
} from '../types';
import { ReducerState, Uuid } from './reducerTypes';
import { isTruthy } from '@ardoq/common-helpers';
import {
  findFilterDefinitionByName,
  QueryBuilderFilterDefinitionGroup,
  QueryBuilderFilterType,
  ReferenceTypeFilterIds,
} from '../filterUtils';
import { mapValues, omit, without } from 'lodash';
import { DndContextState } from '../dndUtils/types';
import {
  APIEntityType,
  BooleanOperator,
  Operator,
  QueryBuilderQuery,
  QueryBuilderRuleValue,
  QueryBuilderRow,
  QueryBuilderRule,
  SearchContextRule,
  QueryBuilderSubquery,
  ReferenceFilterIds,
} from '@ardoq/api-types';

const isValueValid = (
  value?: QueryBuilderRuleValue
): value is QueryBuilderRuleValue => {
  if (Array.isArray(value)) {
    return value.every(subValue => isValueValid(subValue));
  }
  return (
    value === null ||
    value === 0 ||
    value === false ||
    Boolean(value) ||
    value === ''
  );
};

export const isBetweenValueValid = (value?: QueryBuilderRuleValue) => {
  if (Array.isArray(value) && value.length === 2) {
    return value.every(
      subValue => typeof subValue === 'string' && subValue !== ''
    );
  }

  return false;
};

export const isInternalRuleValid = (
  rule: Omit<InternalRule, 'uuid'>
): rule is {
  field: string;
  operator: Operator;
  value: QueryBuilderRuleValue;
} => {
  if (
    rule.operator &&
    [Operator.BETWEEN, Operator.BETWEEN_INCLUSIVE].includes(rule.operator)
  ) {
    return Boolean(isBetweenValueValid(rule.value) && rule.field);
  }

  return Boolean(isValueValid(rule.value) && rule.operator && rule.field);
};

export const getUuid = uuidv4;

export const getEmptyQuery = (uuid: string): InternalQuery => ({
  uuid: uuid,
  condition: BooleanOperator.AND,
  rules: [],
});

export const getEmptyRule = (partial: InternalRule): InternalRule =>
  ({ ...partial }) as InternalRule;

const getIsQuery = (row: InternalRule): row is InternalQuery =>
  Boolean((row as InternalQuery).condition);

const getIsRule = (row: InternalRule): row is InternalRule => !getIsQuery(row);

export const toQuery = <T extends InternalQuery>(row: InternalRow) =>
  getIsQuery(row) ? (row as T) : null;

export const toRule = <T extends InternalRule>(row: InternalRow) =>
  getIsRule(row) ? (row as T) : null;

const composeInternalRows = (
  parentUuid: Uuid,
  reducerState: ReducerState
): InternalRow[] => {
  const rowIds = reducerState.tree[parentUuid];
  return rowIds.map(rowId => {
    const row = reducerState.map[rowId];
    const query = toQuery(row);
    return query
      ? { ...query, rules: composeInternalRows(rowId, reducerState) }
      : row;
  });
};

export const reducerStateToInternalQuery = (
  reducerState: ReducerState
): InternalQuery => {
  const { rootUuid, map } = reducerState;
  return {
    ...map[rootUuid],
    rules: composeInternalRows(rootUuid, reducerState),
  } as InternalQuery;
};

export const inputAndFilterByQueryBuilderFilterType = (
  filterType: QueryBuilderFilterType
) => {
  return {
    input: INPUT_BY_FILTER_TYPE[filterType],
    type: TYPE_BY_FILTER_TYPE[filterType],
  };
};

const INPUT_BY_FILTER_TYPE: Record<QueryBuilderFilterType, string> = {
  [QueryBuilderFilterType.DATE]: 'text',
  [QueryBuilderFilterType.DATE_TIME]: 'text',
  [QueryBuilderFilterType.DATE_RANGE]: 'text',
  [QueryBuilderFilterType.ASYNC_COMPONENT_NAME]: 'text',
  [QueryBuilderFilterType.ASYNC_COMPONENT_KEY]: 'text',
  [QueryBuilderFilterType.ASYNC_WORKSPACE_NAME]: 'text',
  [QueryBuilderFilterType.ASYNC_USER]: 'text',
  [QueryBuilderFilterType.ASYNC_TAGS]: 'text',
  [QueryBuilderFilterType.ASYNC_COMPONENT_OID]: 'text',
  [QueryBuilderFilterType.ASYNC_REFERENCE_OID]: 'text',
  [QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID]: 'text',
  [QueryBuilderFilterType.ASYNC_PARENT]: 'text',
  [QueryBuilderFilterType.ASYNC_TARGET]: 'text',
  [QueryBuilderFilterType.ASYNC_SOURCE]: 'text',
  [QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE]: 'text',
  [QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME]: 'text',
  [QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME]: 'text',
  [QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME]: 'text',
  [QueryBuilderFilterType.STRING]: 'text',
  [QueryBuilderFilterType.SELECT]: 'select',
  [QueryBuilderFilterType.SELECT_MULTIPLE]: 'select',
  [QueryBuilderFilterType.NUMBER]: 'number',
  [QueryBuilderFilterType.BOOLEAN]: 'select',
  [QueryBuilderFilterType.READONLY]: 'text',
};

const TYPE_BY_FILTER_TYPE: Record<QueryBuilderFilterType, string> = {
  [QueryBuilderFilterType.DATE]: 'date',
  [QueryBuilderFilterType.DATE_TIME]: 'date',
  [QueryBuilderFilterType.DATE_RANGE]: 'date',
  [QueryBuilderFilterType.ASYNC_COMPONENT_NAME]: 'string',
  [QueryBuilderFilterType.ASYNC_COMPONENT_KEY]: 'string',
  [QueryBuilderFilterType.ASYNC_WORKSPACE_NAME]: 'string',
  [QueryBuilderFilterType.ASYNC_USER]: 'string',
  [QueryBuilderFilterType.ASYNC_TAGS]: 'string',
  [QueryBuilderFilterType.ASYNC_COMPONENT_OID]: 'string',
  [QueryBuilderFilterType.ASYNC_REFERENCE_OID]: 'string',
  [QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID]: 'string',
  [QueryBuilderFilterType.ASYNC_PARENT]: 'string',
  [QueryBuilderFilterType.ASYNC_TARGET]: 'string',
  [QueryBuilderFilterType.ASYNC_SOURCE]: 'string',
  [QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE]: 'string',
  [QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME]: 'string',
  [QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME]: 'string',
  [QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME]: 'string',
  [QueryBuilderFilterType.STRING]: 'string',
  [QueryBuilderFilterType.SELECT]: 'string',
  [QueryBuilderFilterType.SELECT_MULTIPLE]: 'string',
  [QueryBuilderFilterType.NUMBER]: 'number',
  [QueryBuilderFilterType.BOOLEAN]: 'boolean',
  [QueryBuilderFilterType.READONLY]: 'string',
};

// This is basically an extra mapping due to the
// checkbox field (aka QueryBuilderFilterType.BOOLEAN)
// type having a custom operators
export const getRulePropsFromInternalRule = (
  rule: Omit<InternalRule, 'uuid'>
): {
  operator: Operator;
  value: QueryBuilderRuleValue;
  field: string;
  targetEntityType?: APIEntityType.COMPONENT_TYPE | APIEntityType.COMPONENT;
} | null => {
  if (!isInternalRuleValid(rule)) return null;
  const { operator, field } = rule;
  // Only for checkbox field type
  if (operator === Operator.CHECKED) {
    return { field, operator: Operator.EQUAL, value: true };
  }
  // Only for checkbox field type
  if (operator === Operator.UNCHECKED) {
    return { field, operator: Operator.EQUAL, value: false };
  }
  // Only for checkbox field type
  if (operator === Operator.UNSET) {
    return { field, operator: Operator.IS_EMPTY, value: null };
  }
  return rule;
};

// The reverse of `getRulePropsFromInternalRule`.
export const getInternalRuleFromRuleProps = <
  T extends {
    type: string;
    value: QueryBuilderRuleValue;
  },
>(
  rule: T
) => {
  const { type, value } = rule;
  if (type !== 'boolean') return rule;

  if (value === true) {
    return { ...rule, operator: Operator.CHECKED, value: undefined };
  }

  if (value === false) {
    return { ...rule, operator: Operator.UNCHECKED, value: undefined };
  }

  return { ...rule, operator: Operator.UNSET, value: undefined };
};

const internalRuleToRule = (
  rule: InternalRule,
  filterGroups: QueryBuilderFilterDefinitionGroup[],
  holdInvalidRules?: boolean
): QueryBuilderRule | null => {
  const filter = findFilterDefinitionByName(filterGroups, rule.field);
  const props = getRulePropsFromInternalRule(rule);

  if (holdInvalidRules && props && !filter) {
    return {
      id: props.field,
      field: props.field,
      operator: props.operator,
      value: props.value,
      input: '',
      type: '',
    };
  }
  if (!filter || !props) return null;

  return {
    id: props.field,
    field: props.field,
    input: INPUT_BY_FILTER_TYPE[filter.filterType],
    type: TYPE_BY_FILTER_TYPE[filter.filterType],
    operator: props.operator,
    value: props.value,
    ...(props.targetEntityType
      ? { targetEntityType: props.targetEntityType }
      : {}),
    ...(rule.referenceTypeName
      ? { referenceTypeName: rule.referenceTypeName }
      : {}),
  };
};

const composeRows = (
  parentUuid: Uuid,
  reducerState: ReducerState,
  filterGroups: QueryBuilderFilterDefinitionGroup[],
  holdInvalidRules?: boolean
): QueryBuilderRow[] => {
  const rowIds = reducerState.tree[parentUuid];
  return rowIds
    .map(rowId => {
      const row = reducerState.map[rowId];
      const query = toQuery(row);
      return query
        ? {
            ...omit(query, 'uuid'),
            rules: composeRows(
              rowId,
              reducerState,
              filterGroups,
              holdInvalidRules
            ),
          }
        : internalRuleToRule(row, filterGroups, holdInvalidRules);
    })
    .filter(isTruthy);
};

export const getSearchContextRule = (
  searchContext: SearchContext
): SearchContextRule =>
  ({
    field: 'type',
    id: 'type',
    input: 'select',
    operator: Operator.EQUAL,
    type: 'string',
    value: searchContext,
  }) as const;

export const getIsReducerStateValid = (reducerState: ReducerState) =>
  Object.values(reducerState.map).filter(getIsRule).every(isInternalRuleValid);

export const isReferenceTypeFilterId = (
  filterId: string | undefined
): filterId is ReferenceTypeFilterIds =>
  filterId === ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE ||
  filterId === ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE ||
  filterId === ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE ||
  filterId === ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE;

export const isHasReferenceFilterId = (filterId: string | undefined) =>
  filterId === ReferenceFilterIds.HAS_REFERENCE ||
  filterId === ReferenceFilterIds.HAS_NO_REFERENCE;

export const getIsEmpty = (query: QueryBuilderQuery) =>
  !query.rules[1].rules.length;

export const reducerStateToQuery = (
  reducerState: ReducerState,
  filterGroups: QueryBuilderFilterDefinitionGroup[],
  holdInvalidRules?: boolean
): QueryBuilderQuery => {
  const { rootUuid, map, searchContext } = reducerState;
  const subquery = {
    ...omit(map[rootUuid], 'uuid'),
    rules: composeRows(rootUuid, reducerState, filterGroups, holdInvalidRules),
  } as QueryBuilderSubquery;

  return {
    condition: BooleanOperator.AND,
    rules: [getSearchContextRule(searchContext), subquery],
  };
};

const findParentUuid = (state: ReducerState, uuid: string) =>
  Object.entries(state.tree).find(([, childrenUuids]) =>
    childrenUuids.includes(uuid)
  )?.[0] || null;

export const removeRowFromState = (
  state: ReducerState,
  uuid: string
): ReducerState => {
  const parentUuid = findParentUuid(state, uuid);
  const childrenUuids = state.tree[uuid] || [];
  const map = omit(state.map, [uuid]);
  const tree = parentUuid
    ? {
        ...omit(state.tree, [uuid]),
        [parentUuid]: without(state.tree[parentUuid], uuid),
      }
    : omit(state.tree, [uuid]);

  return childrenUuids.reduce(removeRowFromState, { ...state, map, tree });
};

export const addNewRowToState = (
  state: ReducerState,
  parentUuid: string,
  row: InternalRow
): ReducerState => {
  const addedRowUuid = row.uuid || getUuid();
  const isAddingQuery = getIsQuery(row);
  const subrows = isAddingQuery ? (row as InternalQuery).rules : [];
  const stateWithAddedRow = {
    ...state,
    map: { ...state.map, [addedRowUuid]: { ...row, uuid: addedRowUuid } },
    tree: {
      ...state.tree,
      [parentUuid]: [...state.tree[parentUuid], addedRowUuid],
      ...(isAddingQuery ? { [addedRowUuid]: [] } : {}),
    },
  };
  return subrows.reduce(
    (state, row) => addNewRowToState(state, addedRowUuid, row),
    stateWithAddedRow
  );
};

const enhanceRuleWithDefaultOperator = (
  row: InternalRow,
  filterGroups: QueryBuilderFilterDefinitionGroup[]
): InternalRow => {
  if (getIsRule(row) && row.field) {
    const filter = findFilterDefinitionByName(filterGroups, row.field);
    if (!filter) return row;
    return { ...row, operator: row.operator || filter.operators[0] };
  }
  return row;
};

const getFilterTypeFromRule = (
  rule: QueryBuilderRule,
  filterGroups: QueryBuilderFilterDefinitionGroup[]
): QueryBuilderFilterType | null => {
  for (const filterGroup of filterGroups) {
    const filter = filterGroup.filters.find(({ id }) => rule.id === id);
    if (filter) return filter.filterType;
  }
  return null;
};

const mapBooleanFilterToCheckboxRow = (
  rule: QueryBuilderRule,
  uuid: string
): InternalRow => {
  if (rule.operator === Operator.EQUAL && rule.value === true) {
    return { uuid, ...rule, operator: Operator.CHECKED, value: true };
  }
  if (rule.operator === Operator.EQUAL && rule.value === false) {
    return { uuid, ...rule, operator: Operator.UNCHECKED, value: false };
  }
  // UNSET
  return { uuid, ...rule, operator: Operator.UNSET, value: null };
};

const getIsSubquery = (row: QueryBuilderRow): row is QueryBuilderSubquery =>
  Boolean((row as any)?.condition);

const rowToInternalRow = (
  row: QueryBuilderRow,
  filterGroups: QueryBuilderFilterDefinitionGroup[]
): InternalRow => {
  const uuid = (row as unknown as InternalRow).uuid || getUuid();
  if (getIsSubquery(row)) {
    const rules = row.rules.map(rule => rowToInternalRow(rule, filterGroups));
    return { uuid, ...row, rules };
  }
  const type = getFilterTypeFromRule(row, filterGroups);
  if (type === QueryBuilderFilterType.BOOLEAN) {
    return mapBooleanFilterToCheckboxRow(row, uuid);
  }
  return { uuid, ...row };
};

export const queryToReducerState = (
  initialSeachContext: SearchContext,
  initalQuery: QueryBuilderSubquery,
  filterGroups: QueryBuilderFilterDefinitionGroup[],
  maxSubqueryNestingLevel: number
): ReducerState => {
  const rootUuid = (initalQuery as InternalQuery).uuid || getUuid();
  const initialReducerState: ReducerState = {
    rootUuid,
    searchContext: initialSeachContext,
    map: {
      [rootUuid]: {
        uuid: rootUuid,
        condition: initalQuery.condition,
        rules: [],
      },
    },
    tree: { [rootUuid]: [] },
    maxSubqueryNestingLevel,
  };
  const initialState = initalQuery.rules.reduce(
    (state, row) =>
      addNewRowToState(state, rootUuid, rowToInternalRow(row, filterGroups)),
    initialReducerState
  );
  return fillEmptyQueries({
    ...initialState,
    map: mapValues(initialState.map, row =>
      enhanceRuleWithDefaultOperator(row, filterGroups)
    ),
  });
};

export const fillEmptyQueries = (state: ReducerState): ReducerState => {
  const queryUuidsWithoutRules = Object.entries(state.tree)
    .map(([parentUuid, childrenUuids]) =>
      childrenUuids.length === 0 ? parentUuid : null
    )
    .filter(isTruthy);

  return queryUuidsWithoutRules.reduce(
    (acc, uuid) =>
      addNewRowToState(acc, uuid, getEmptyRule({ uuid: getUuid() })),
    state
  );
};

export const findParentSubqueryIdOfRowId = (
  rowId: string,
  state: ReducerState
) =>
  Object.entries(state.tree).find(([, childrenIds]) =>
    childrenIds.includes(rowId)
  )?.[0] || null;

const insertElementToArray = <T>(array: T[], index: number, item: T) => {
  const resultArray = [...array];
  resultArray.splice(index, 0, item);
  return resultArray;
};

export const calculateMoveRow = (
  state: ReducerState,
  {
    draggedRowId,
    position,
    overRuleId,
  }: Pick<DndContextState, 'draggedRowId' | 'position' | 'overRuleId'>
): ReducerState => {
  if (!draggedRowId || !position || !overRuleId) return state;
  const parentSubqueryIdOfDraggedRow = findParentSubqueryIdOfRowId(
    draggedRowId,
    state
  );
  const parentSubqueryIdOfOverRuleId = findParentSubqueryIdOfRowId(
    overRuleId,
    state
  );
  if (!parentSubqueryIdOfDraggedRow || !parentSubqueryIdOfOverRuleId) {
    return state;
  }
  const indexOfOverRuleId = without(
    state.tree[parentSubqueryIdOfOverRuleId],
    draggedRowId
  ).indexOf(overRuleId);

  return {
    ...state,
    tree: {
      ...state.tree,
      [parentSubqueryIdOfDraggedRow]: without(
        state.tree[parentSubqueryIdOfDraggedRow],
        draggedRowId
      ),
      [parentSubqueryIdOfOverRuleId]: insertElementToArray(
        without(state.tree[parentSubqueryIdOfOverRuleId], draggedRowId),
        position === 'below' ? indexOfOverRuleId + 1 : indexOfOverRuleId,
        draggedRowId
      ),
    },
  };
};

export const getAllFieldsFromQuery = (
  query: QueryBuilderQuery | QueryBuilderSubquery | null
): Array<QueryBuilderRule> => {
  if (!query) return [];
  return query.rules.reduce((acc: Array<QueryBuilderRule>, curr) => {
    if (getIsSubquery(curr)) {
      return [...acc, ...getAllFieldsFromQuery(curr)];
    }
    return [...acc, curr];
  }, []);
};
